import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Mail, Phone, MapPin, Users, Clock, Send, User } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "../hooks/use-toast";
import { api } from "../lib/api";
import { supabase } from "@/integrations/supabase/client";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import PageLoader from "@/components/PageLoader";
import { useImagePreloader } from "@/hooks/useImagePreloader";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface RegionalContact {
  region: string;
  coordinator: string;
  email: string;
  phone: string;
  states: string[];
  meetingTime: string;
}

const Contact = () => {
  const [president, setPresident] = useState<any>(null);
  const [treasurer, setTreasurer] = useState<any>(null);
  const [regionalContacts, setRegionalContacts] = useState<RegionalContact[]>([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    region: "",
    country: "",
    stateProvince: "",
    subject: "",
    message: "",
    areaOfInquiry: "",
  });
  const [locationType, setLocationType] = useState<"USA" | "UESI, India" | "Other (International)">("USA");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchLeadership = async () => {
      // Read from sanitized public view (excludes email/phone/notes)
      const { data } = await (supabase as any)
        .from("members_public")
        .select("*")
        .in("role", ["president", "treasurer"])
        .is("end_year", null);

      if (data) {
        setPresident(data.find((m: any) => m.role === "president"));
        setTreasurer(data.find((m: any) => m.role === "treasurer"));
      }
    };

    const fetchRegionalContacts = async () => {
      const { data: regionsData } = await supabase.from("regions").select("*").order("name");

      if (!regionsData) return;

      const contactsPromises = regionsData.map(async (region: any) => {
        // Read coordinators from sanitized view via member_roles join
        const { data: coordinatorRoles } = await (supabase as any)
          .from("member_roles")
          .select(
            `
            role,
            member_id,
            members_public!inner(
              id,
              full_name,
              region_id,
              is_active
            )
          `,
          )
          .eq("members_public.region_id", region.id)
          .in("role", [
            "regional_president",
            "regional_vice_president",
            "regional_secretary",
            "regional_joint_secretary",
            "regional_treasurer",
          ])
          .eq("members_public.is_active", true);

        const coordinators =
          coordinatorRoles?.map((item: any) => ({
            ...item.members_public,
            role: item.role,
          })) || [];

        const primaryCoordinator = coordinators?.find((c: any) => c.role === "regional_president") || coordinators?.[0];

        if (primaryCoordinator) {
          return {
            region: region.name,
            coordinator: primaryCoordinator.full_name,
            // PII (email/phone) intentionally hidden from public; visitors should use the contact form
            email: "Use the contact form below",
            phone: "Use the contact form below",
            states: region.states || [],
            meetingTime: "Contact for details",
          };
        }

        return null;
      });

      const contacts = (await Promise.all(contactsPromises)).filter(Boolean) as RegionalContact[];
      setRegionalContacts(contacts);
    };

    const loadData = async () => {
      await Promise.all([fetchLeadership(), fetchRegionalContacts()]);
      setLoading(false);
    };

    loadData();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsSubmitting(true);

    // Minimum 1 second loading for better UX
    const minLoadingTime = new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      // Determine region based on selected state or location type
      let regionLabel = locationType;
      if (locationType === "USA" && selectedState) {
        const matchingContact = regionalContacts.find((contact) => contact.states.includes(selectedState));
        regionLabel = matchingContact?.region || "";
      }

      // Find region_id from region name (if exists)
      const { data: regionsData } = regionLabel
        ? await supabase.from("regions").select("id").eq("name", regionLabel).maybeSingle()
        : ({ data: null } as any);

      // Build message with extra international details if provided
      const extraInfo: string[] = [];
      if (locationType !== "USA") {
        if (formData.country) extraInfo.push(`Country: ${formData.country}`);
        if (formData.stateProvince) extraInfo.push(`State/Province: ${formData.stateProvince}`);
      }
      const messageToSave = extraInfo.length ? `${formData.message}\n\n${extraInfo.join("\n")}` : formData.message;

      // Insert into contact_messages table
      const { error: insertError } = await supabase.from("contact_messages").insert({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        region_id: regionsData?.id || null,
        subject: formData.subject,
        message: messageToSave,
        area_of_inquiry: formData.areaOfInquiry,
        status: "new",
      });

      if (insertError) throw insertError;

      // Send email notifications via edge function
      const { error: emailError } = await supabase.functions.invoke("send-contact-notifications", {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: messageToSave,
          area_of_inquiry: formData.areaOfInquiry,
          region_id: regionsData?.id || null,
          region_name: regionLabel || null,
        },
      });

      if (emailError) {
        console.error("Email notification error:", emailError);
        // Don't fail the whole submission if email fails
      }

      // Wait for minimum loading time
      await minLoadingTime;

      toast({
        title: "Message Sent Successfully!",
        description:
          "Our team has been notified and will reach back to you soon. Please check your email for confirmation.",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        region: "",
        country: "",
        stateProvince: "",
        subject: "",
        message: "",
        areaOfInquiry: "",
      });
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to send message", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // Preload coordinator images
  const coordinatorImages = regionalContacts
    .map((contact) => contact as any)
    .filter((c) => c.profile_image_url)
    .map((c) => c.profile_image_url);

  const { imagesLoaded } = useImagePreloader({
    imageUrls: coordinatorImages,
    enabled: !loading,
  });

  if (loading || !imagesLoaded) {
    return <PageLoader message="Loading contact information..." />;
  }

  return (
    <div className="min-h-screen bg-background">
      <section className="bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan py-20">
        <div className="container mx-auto px-4">
          <div className="text-center text-white space-y-6">
            <h1 className="text-4xl lg:text-6xl font-bold">Get In Touch</h1>
            <p className="text-xl lg:text-2xl text-blue-100 max-w-3xl mx-auto">
              Connect with your regional coordinator or reach out to our national team. We're here to help you find
              fellowship and grow in faith.
            </p>
            <link rel="canonical" href={typeof window !== "undefined" ? window.location.href : ""} />
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="w-5 h-5" />
                Send Us a Message
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Full Name <span className="text-destructive">*</span>
                    </label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Email <span className="text-destructive">*</span>
                    </label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Phone</label>
                    <div className="border border-input rounded-md">
                      <PhoneInput
                        international
                        defaultCountry="US"
                        value={formData.phone}
                        onChange={(value) => setFormData((prev) => ({ ...prev, phone: value || "" }))}
                        className="flex h-10 w-full rounded-md bg-background px-3 py-2 text-sm focus:outline-none"
                        placeholder="+1 123 456 7890"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Location</label>
                    <select
                      name="locationType"
                      value={locationType}
                      onChange={(e) => setLocationType(e.target.value as any)}
                      className="w-full px-3 py-2 border border-input rounded-md"
                    >
                      <option value="USA">USA</option>
                      <option value="UESI, India">UESI, India</option>
                      <option value="Other (International)">Other (International)</option>
                    </select>
                  </div>
                </div>

                {locationType === "USA" && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      State <span className="text-destructive">*</span>
                    </label>
                    <select
                      name="state"
                      value={selectedState}
                      onChange={(e) => {
                        const state = e.target.value;
                        setSelectedState(state);
                        // Find the region that contains this state
                        const matchingContact = regionalContacts.find((contact) => contact.states.includes(state));
                        setFormData((prev) => ({
                          ...prev,
                          region: matchingContact?.region || "",
                        }));
                      }}
                      className="w-full px-3 py-2 border border-input rounded-md"
                      required
                    >
                      <option value="">Select your state</option>
                      {regionalContacts
                        .flatMap((contact) =>
                          contact.states.map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          )),
                        )
                        .sort((a, b) => a.props.children.localeCompare(b.props.children))}
                    </select>
                  </div>
                )}

                {locationType !== "USA" && (
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        Country <span className="text-destructive">*</span>
                      </label>
                      <Input
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        placeholder="Your country"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">
                        State/Province <span className="text-destructive">*</span>
                      </label>
                      <Input
                        name="stateProvince"
                        value={formData.stateProvince}
                        onChange={handleChange}
                        placeholder="Your state or province"
                        required
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Area of Inquiry <span className="text-destructive">*</span>
                  </label>
                  <select
                    name="areaOfInquiry"
                    value={formData.areaOfInquiry}
                    onChange={handleChange}
                    required
                    className="w-full px-3 py-2 border border-input rounded-md bg-background"
                  >
                    <option value="">Select an area</option>
                    <option value="Women's Ministry">Women's Ministry</option>
                    <option value="Volunteer">Volunteer</option>
                    <option value="Events and Conferences">Events and Conferences</option>
                    <option value="Finding a local community">Finding a local community</option>
                    <option value="General Inquiry">General Inquiry</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Subject <span className="text-destructive">*</span>
                  </label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What's this about?"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Message <span className="text-destructive">*</span>
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your inquiry..."
                    className="min-h-32"
                    required
                  />
                </div>

                <Button type="submit" className="w-full bg-primary" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <div className="flex items-center gap-2">
                      <LoadingSpinner size={16} />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    "Send Message"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* National Office & Quick Info */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  National Office
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-medium">Address</div>
                    <div className="text-sm text-muted-foreground">
                      1921 Narrows Lane
                      <br />
                      Silver Spring, MD 20906
                    </div>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-medium">Email</div>
                    <a href="mailto:admofuesi2025@gmail.com" className="text-sm text-primary hover:underline">
                      admofuesi2025@gmail.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Website Feedback Section */}
            <Card className="border-2 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Provide Feedback on the New Website
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Your feedback is valuable! Please take a moment to share your thoughts about our newly built website.
                  Help us improve your experience by submitting your feedback.
                </p>
                <Button asChild className="w-full bg-primary">
                  <a href="https://feedback.ofuesiusa.net/" target="_blank" rel="noopener noreferrer">
                    Submit Feedback
                  </a>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Quick Links</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button asChild variant="outline" className="w-full justify-start">
                  <a href="/prayer">Submit Prayer Request</a>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start">
                  <a href="/regions">Find Your Region</a>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start">
                  <a href="/conference">Conference Registration</a>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start">
                  <a href="/admin">Regional Admin Login</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Regional Contacts */}
        <div className="mt-16">
          <h2 className="text-3xl font-bold text-center mb-12">Regional Coordinators</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {regionalContacts.map((contact, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{contact.region}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    {contact.coordinator && (
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary" />
                        <span className="font-medium">{contact.coordinator}</span>
                      </div>
                    )}

                    {contact.email && contact.email !== "Contact for details" && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-primary" />
                        <a href={`mailto:${contact.email}`} className="text-sm hover:underline">
                          {contact.email}
                        </a>
                      </div>
                    )}

                    {contact.phone && contact.phone !== "Contact for details" && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-primary" />
                        <a href={`tel:${contact.phone}`} className="text-sm hover:underline">
                          {contact.phone}
                        </a>
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-sm font-medium mb-2">Serving States:</p>
                    <div className="flex flex-wrap gap-1">
                      {contact.states.map((state) => (
                        <Badge key={state} variant="primary" className="text-xs">
                          {state}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {contact.email && contact.email !== "Contact for details" && (
                    <Button asChild variant="outline" size="sm" className="w-full">
                      <a href={`mailto:${contact.email}`}>Contact Coordinator</a>
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
